import React, { useCallback } from 'react';
import ValidInputError from './ValidInputError';
import { ValidOptionalInput } from './types';
import ValidErrorMarker from './ValidErrorMarker';
import ValidInputLabel from './ValidInputLabel';

// extend Blob to include browser file properties
export interface InputFileBlob extends Blob {
    readonly name: string;
    readonly lastModified: number;
    readonly webkitRelativePath: string;
}

export type AllowedFileType = '.csv' | '.xlsx' | '.txt' | '.json' | '';
export type AllowedFileSize = {
    min: number; // in bytes
    max: number; // in bytes
};

// TYPE
export type ValidPropFile = {
    key: string;
    lbl?: string;
    file: InputFileBlob | null;
    types: AllowedFileType[];
    size: AllowedFileSize;
    err: string;
};

// file size defaults
export const FILE_SIZE_MIN_DEFAULT = 10; // 10 bytes
export const FILE_SIZE_MAX_DEFAULT = 1024 * 1024; // 1 MB
export const FILE_SIZE_MAX_MEDIUM = 5 * 1024 * 1024; // 5 MB
export const FILE_SIZE_MAX_LARGE = 10 * 1024 * 1024; // 10 MB

// VIEW MODEL
export function validPropFileVM(
    key: string,
    lbl?: string,
    allowedTypes?: AllowedFileType[],
    fileSize?: AllowedFileSize,
    err?: string,
): ValidPropFile {
    return {
        key: key,
        lbl: !!lbl ? lbl : '',
        file: null,
        types: Array.isArray(allowedTypes) ? allowedTypes : ['.csv'],
        size: fileSize || { min: FILE_SIZE_MIN_DEFAULT, max: FILE_SIZE_MAX_DEFAULT },
        err: !!err ? err : '',
    };
}

// INPUT COMPONENT
export type ValidInputFileParams = ValidOptionalInput & {
    prop: ValidPropFile;
    disabled?: boolean;
    placeholder?: string;
    onChange: (prop: string, fileBlob: InputFileBlob | null) => void;
    alternateLabel?: string;
    fullWidth?: boolean;
    hideError?: boolean;
};

const ValidInputFile = ({
    prop,
    disabled,
    placeholder,
    onChange,
    alternateLabel,
    checked,
    onCheckboxChange,
    fullWidth,
    hideError = false,
}: ValidInputFileParams): JSX.Element => {
    const id = 'vpFile' + prop.key;
    const promptTxt = !!placeholder ? placeholder : 'Select data file...';
    const showCheckbox = !!onCheckboxChange && checked !== undefined;

    // Data file types
    const acceptedTypes = prop.types.join(',');

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>): void => {
            const file = e.target.files?.[0] || null;
            onChange(prop.key, file);
        },
        [onChange, prop.key],
    );

    const handleCheckboxChange = useCallback((): void => {
        if (showCheckbox) {
            onCheckboxChange(!checked);
        }
    }, [checked, onCheckboxChange, showCheckbox]);

    return (
        <div
            id={id}
            className="component-valid-input component-valid-input-file"
        >
            <div className={`input-wrapper ${fullWidth ? 'full-width' : ''}`}>
                {prop.lbl ? (
                    <ValidInputLabel
                        label={alternateLabel ?? prop.lbl}
                        onClick={showCheckbox ? handleCheckboxChange : undefined}
                        showCheckbox={showCheckbox}
                        checked={checked}
                    />
                ) : null}
                <input
                    name={prop.key}
                    className="valid-input input-file"
                    type="file"
                    disabled={disabled}
                    accept={acceptedTypes}
                    placeholder={promptTxt}
                    onChange={handleChange}
                />
                {hideError ? null : <ValidErrorMarker showMarker={!!prop.err} />}
            </div>
            {hideError ? null : (
                <ValidInputError
                    err={prop.err}
                    showMsg
                />
            )}
        </div>
    );
};

export default ValidInputFile;
